// src/components/admin/AdminSidebar.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Home, Users, Clock, DollarSign,
    Award,
    ChevronLeft, ChevronRight, Building, UserCheck, TrendingUp,
    CalendarClock, ClipboardCheck, ListTodo, FileText, Package, GitBranch
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import approvalService from '../../services/approval.service';
import logo from '../../oakhr-logo-full.svg';
import logo2 from '../../oakhr-tree.svg';

const AdminSidebar = ({ isOpen, onToggle, user }) => {
    const { theme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const isSuperAdmin = authUser?.role_name === 'Super Admin';
    const [pendingCount, setPendingCount] = useState(0);
    const [pendingBreakdown, setPendingBreakdown] = useState({ leave: 0, attendance: 0, other: 0 });
    const [hoveredItem, setHoveredItem] = useState(null);
    const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
    const hoverTimeoutRef = useRef(null);
    const flyoutRef = useRef(null);

    // Fetch pending approvals count
    useEffect(() => {
        const fetchPendingCount = async () => {
            try {
                const response = await approvalService.getPendingApprovalsCount();
                if (response.success) {
                    setPendingCount(response.total || 0);
                    setPendingBreakdown({
                        leave: response.leave || 0,
                        attendance: response.attendance || 0,
                        other: response.other || 0,
                    });
                }
            } catch (error) {
                console.error('Error fetching pending approvals count:', error);
            }
        };

        fetchPendingCount();
        const interval = setInterval(fetchPendingCount, 60000);
        return () => clearInterval(interval);
    }, []);

    const menuItems = [
        { path: '/admin/dashboard', icon: Home, label: 'Dashboard', description: 'Admin overview' },
        { path: '/admin/employees', icon: Users, label: 'Employees', description: 'Manage employees', subItems: [
            { tab: 'employees', label: 'Employees' },
            { tab: 'analytics', label: 'Analytics' },
            { tab: 'recruitment', label: 'Recruitment' },
        ]},
        { path: '/admin/onboarding', icon: UserCheck, label: 'Onboarding', description: 'Onboarding management', subItems: [
            { tab: 'pending', label: 'Pending' },
            { tab: 'completed', label: 'Completed' },
            { tab: 'all', label: 'All Employees' },
            { tab: 'configure', label: 'Configure' },
        ]},
        { path: '/admin/attendance', icon: Clock, label: 'Attendance', description: 'Attendance tracking', subItems: [
            { tab: 'overview', label: 'Overview' },
            { tab: 'analytics', label: 'Analytics' },
            { tab: 'holidays', label: 'Holidays' },
            { tab: 'leavePlans', label: 'Leave Plans' },
            { tab: 'regulate', label: 'Regulate' },
            { tab: 'geo', label: 'Geo Location' },
            { tab: 'biometrics', label: 'Biometrics' },
        ]},
        { path: '/admin/shifts', icon: CalendarClock, label: 'Shifts', description: 'Shift management' },
        { path: '/admin/approvals', icon: ClipboardCheck, label: 'Approvals', description: 'Leave & request approvals', subItems: [
            { tab: 'leave', label: 'Leave', badgeKey: 'leave' },
            { tab: 'attendance', label: 'Attendance', badgeKey: 'attendance' },
            { tab: 'other', label: 'Other', badgeKey: 'other' },
        ]},
        { path: '/admin/flows', icon: GitBranch, label: 'Automations', description: 'Workflow builder & automations', subItems: [
            { tab: 'dashboard', label: 'Dashboard' },
            { tab: 'workflows', label: 'Workflows' },
            { tab: 'approvals', label: 'Approval Chains' },
            { tab: 'automations', label: 'Automations' },
            { tab: 'templates', label: 'Templates' },
            { tab: 'history', label: 'History' },
        ]},
        { path: '/admin/performance', icon: TrendingUp, label: 'Performance', description: 'Performance reviews & templates', subItems: [
            { tab: 'pending', label: 'Pending Reviews' },
            { tab: 'reviews', label: 'Confirmed Reviews' },
            { tab: 'templates', label: 'Templates' },
            { tab: 'analytics', label: 'Analytics' },
        ]},
        { path: '/admin/payroll', icon: Award, label: 'Compensation', description: 'Salary & benefits', subItems: [
            { tab: 'compensation', label: 'Compensation' },
            { tab: 'pay-groups', label: 'Pay Groups' },
            { tab: 'pf', label: 'PF Management' },
            { tab: 'tds', label: 'TDS Management' },
        ]},
        { path: '/admin/document-builder', icon: FileText, label: 'Document Builder', description: 'Create HR documents', subItems: [
            { tab: 'templates', label: 'Templates' },
            { tab: 'generate', label: 'Generate' },
            { tab: 'custom', label: 'Custom Letter' },
        ]},
        { path: '/admin/org-view', icon: Building, label: 'Org View', description: 'Organization structure', subItems: [
            { tab: 'organization', label: 'Organization' },
            { tab: 'functional', label: 'Functional' },
        ]},
        { path: '/admin/payrollrun', icon: DollarSign, label: 'Payroll', description: 'Payroll processing', subItems: [
            { tab: 'analytics', label: 'Analytics' },
            { tab: 'run', label: 'Payroll Run' },
        ]},
        { path: '/admin/team-taskbox', icon: ListTodo, label: 'Team Taskbox', description: 'Team task management' },
        { path: '/admin/assets', icon: Package, label: 'Assets', description: 'Asset management', subItems: [
            { tab: 'dashboard', label: 'Dashboard' },
            { tab: 'assets', label: 'Assets' },
            { tab: 'categories', label: 'Categories' },
            { tab: 'maintenance', label: 'Maintenance' },
            { tab: 'audit', label: 'Audit Log' },
        ]},
    ];

    const visibleMenuItems = menuItems.filter(item =>
        !item.superAdminOnly || isSuperAdmin
    );

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const currentTab = new URLSearchParams(location.search).get('tab');

    const handleMouseEnter = useCallback((path, e) => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        const rect = e.currentTarget.getBoundingClientRect();
        setFlyoutPos({ top: rect.top, left: rect.right + 4 });
        setHoveredItem(path);
    }, []);

    const handleMouseLeave = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredItem(null);
        }, 150);
    }, []);

    const handleSubItemClick = useCallback((path, tab) => {
        setHoveredItem(null);
        navigate(`${path}?tab=${tab}`);
    }, [navigate]);

    return (
        <aside className={`${isOpen ? 'w-64' : 'w-20'} h-screen border-r flex flex-col transition-all duration-300 relative z-[60] ${
            theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
        }`}>
            {/* Logo */}
            <div className={`p-6 border-b flex items-center justify-between ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                {isOpen ? (
                    <>
                        <div className="flex flex-col items-center justify-center flex-1">
                            <img src={logo} alt="Logo" className="h-50 w-auto object-contain" />
                        </div>
                        <button
                            onClick={onToggle}
                            className={`p-2 rounded-lg transition-colors focus:outline-none focus:ring-0 outline-none ${
                                theme === 'dark'
                                    ? 'text-gray-300 hover:bg-gray-700'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                    </>
                ) : (
                    <>
                        <img src={logo2} alt="Logo" className="h-8 w-8 object-contain" />
                        <button
                            onClick={onToggle}
                            className={`p-1.5 rounded-lg transition-colors focus:outline-none focus:ring-0 outline-none ${
                                theme === 'dark'
                                    ? 'text-gray-300 hover:bg-gray-700'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </>
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-2">
                {visibleMenuItems.map((item) => {
                    const IconComponent = item.icon;
                    const active = isActive(item.path);
                    const hasSubItems = item.subItems && item.subItems.length > 0;
                    const isHovered = hoveredItem === item.path;

                    return (
                        <div
                            key={item.path}
                            className="relative"
                            onMouseEnter={(e) => hasSubItems && handleMouseEnter(item.path, e)}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button
                                data-tour={item.path}
                                className={`tour-${item.path.replace('/admin/', '').replace('/', '-')} w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors group ${
                                    active
                                        ? theme === 'dark'
                                            ? 'bg-blue-900 text-blue-100'
                                            : 'bg-blue-50 text-blue-700'
                                        : theme === 'dark'
                                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                                onClick={() => navigate(item.path)}
                                title={isOpen ? '' : item.description}
                            >
                                <div className="relative">
                                    <IconComponent className="w-5 h-5 flex-shrink-0" />
                                    {!isOpen && item.path === '/admin/approvals' && pendingCount > 0 && (
                                        <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 text-[10px] font-bold bg-red-600 text-white rounded-full flex items-center justify-center">
                                            {pendingCount > 99 ? '99+' : pendingCount}
                                        </span>
                                    )}
                                </div>
                                {isOpen && (
                                    <>
                                        <span className="font-medium flex-1 text-left">{item.label}</span>
                                        {item.path === '/admin/approvals' && pendingCount > 0 && (
                                            <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                                {pendingCount > 99 ? '99+' : pendingCount}
                                            </span>
                                        )}
                                        {hasSubItems && (
                                            <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
                                                isHovered ? 'translate-x-0.5' : ''
                                            } ${
                                                active
                                                    ? theme === 'dark' ? 'text-blue-300' : 'text-blue-500'
                                                    : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                            }`} />
                                        )}
                                    </>
                                )}
                            </button>

                            {/* Flyout sub-menu */}
                            {hasSubItems && isHovered && (
                                <div
                                    ref={flyoutRef}
                                    className="fixed z-[9999]"
                                    style={{ top: flyoutPos.top - 8, left: flyoutPos.left }}
                                    onMouseEnter={() => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <div className={`rounded-xl shadow-xl border min-w-[190px] overflow-hidden divide-y ${
                                        theme === 'dark'
                                            ? 'bg-gray-800 border-gray-700 shadow-black/30 divide-gray-700'
                                            : 'bg-white border-gray-200 shadow-gray-200/50 divide-gray-100'
                                    }`}>
                                        {item.subItems.map((sub) => {
                                            const isSubActive = isActive(item.path) && currentTab === sub.tab;
                                            return (
                                                <button
                                                    key={sub.tab}
                                                    onClick={() => handleSubItemClick(item.path, sub.tab)}
                                                    className={`w-full text-left px-3 py-1.5 text-xs font-medium transition-colors flex items-center justify-between ${
                                                        isSubActive
                                                            ? theme === 'dark'
                                                                ? 'bg-blue-900/40 text-blue-300'
                                                                : 'bg-blue-50 text-blue-700'
                                                            : theme === 'dark'
                                                                ? 'text-gray-300 hover:bg-gray-700/70 hover:text-white'
                                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                    }`}
                                                >
                                                    <span className="flex-1">{sub.label}</span>
                                                    {sub.badgeKey && pendingBreakdown[sub.badgeKey] > 0 && (
                                                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full leading-none">
                                                            {pendingBreakdown[sub.badgeKey] > 99 ? '99+' : pendingBreakdown[sub.badgeKey]}
                                                        </span>
                                                    )}
                                                    <ChevronRight className="w-3 h-3 flex-shrink-0 opacity-40" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>

        </aside>
    );
};

export default AdminSidebar;
